import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LogOut, LayoutDashboard, Users, MapPin, Truck, AppWindow, FileText, Settings, AlertCircle, Home, ArrowLeft } from 'lucide-react';
import { AppHubButton } from '@/components/common/AppHubButton';
import CustomerManagement from './admin/CustomerManagement';
import LocationManagement from './admin/LocationManagement';
import VehicleManagement from './admin/VehicleManagement';
import AppManagement from './admin/AppManagement';
import SubmissionRepository from './admin/SubmissionRepository';
import Overview from './admin/Overview';
import WorkOrderManagement from './admin/WorkOrderManagement';
import AdminWorkOrderManagement from './admin/AdminWorkOrderManagement';
import AdminWorkOrderForm from './admin/AdminWorkOrderForm';
import VehicleInspectionManagement from './admin/VehicleInspectionManagement';
import FeedbackRepository from './admin/FeedbackRepository';
import PropertyEvaluationManagement from './admin/PropertyEvaluationManagement';
import BankPMManagement from './admin/BankPMManagement';
import AppLauncher from './admin/AppLauncher';
import UserManagement from './admin/UserManagement';
import SystemStatus from './admin/SystemStatus';
import ServiceLocationManagement from './admin/ServiceLocationManagement';
import FollowUpManagement from './admin/FollowUpManagement';
import GlobalSettings from './admin/GlobalSettings';
import IncidentReportManagement from './admin/IncidentReportManagement';
import PMPSchoolsManagement from './admin/PMPSchoolsManagement';
import BuildingIntelligenceManagement from './admin/BuildingIntelligenceManagement';
import PreApplicationManagement from './admin/PreApplicationManagement';
import PMPOfficeManagement from './admin/PMPOfficeManagement';
import InspectionManagement from './admin/InspectionManagement';
import EventServiceSurveyManagement from './admin/EventServiceSurveyManagement';
import ClientCommunicationManagement from './admin/ClientCommunicationManagement';
import WorkOrderDetail from './admin/WorkOrderDetail';
import BuildingAreasManagement from './admin/BuildingAreasManagement';

const AdminDashboard = () => {
  const { user, logout, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const menuItems = [
    { icon: AlertCircle, label: 'System Status', path: '/admin/status', key: 'status' },
    { icon: Users, label: 'Customers', path: '/admin/customers', key: 'customers' },
    { icon: MapPin, label: 'Service Locations', path: '/admin/service-locations', key: 'service-locations' },
    { icon: Truck, label: 'Vehicles', path: '/admin/vehicles', key: 'vehicles' },
    { icon: FileText, label: 'Follow-Ups', path: '/admin/followups', key: 'followups' },
  ];

  // Separate Home and Settings actions
  const homeAction = { icon: Home, label: 'Home', path: '/admin', key: 'home' };
  const settingsAction = { icon: Settings, label: 'Settings', path: '/admin/settings', key: 'settings' };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_tfs-workspace/artifacts/xuj2h70u_TFSI%203-26%20PNG.png" 
                alt="TFS Logo" 
                className="h-12"
              />
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>TFS Administration — Module Configuration</h1>
                <p className="text-sm text-slate-500">Module screens · return to the Admin Hub or App Hub anytime</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button onClick={() => navigate('/tfs-admin')} data-testid="back-to-admin-hub-btn"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-[#0E2A47] hover:bg-slate-50">
                <ArrowLeft size={16} /> <span className="hidden sm:inline">Admin Hub</span>
              </button>
              <AppHubButton variant="light" />
              <div className="text-right">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                <p className="text-xs text-slate-400">{_fmtDate(new Date())}</p>
              </div>
              <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Top Navigation Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            {/* Large Home Icon - Left */}
            <Button
              variant="ghost"
              className="p-3 hover:bg-gray-100 min-w-[80px] min-h-[90px] flex flex-col items-center justify-center gap-1"
              onClick={() => navigate(homeAction.path)}
              data-testid={`nav-${homeAction.key}`}
              title="Home - App Launcher"
              style={{ width: '80px', height: '90px' }}
            >
              <Home className="text-gray-700" style={{ width: '48px', height: '48px' }} />
              <span className="text-xs text-gray-500 font-medium">Home</span>
            </Button>

            {/* Centered Information Buttons */}
            <div className="flex flex-wrap gap-2 justify-center">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.key}
                    variant="outline"
                    className="flex items-center space-x-2 bg-green-100 hover:bg-green-200 border-green-200 text-gray-700"
                    onClick={() => navigate(item.path)}
                    data-testid={`nav-${item.key}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Button>
                );
              })}
            </div>

            {/* Large Settings Icon - Right */}
            <Button
              variant="ghost"
              className="p-3 hover:bg-gray-100 min-w-[80px] min-h-[90px] flex flex-col items-center justify-center gap-1"
              onClick={() => navigate(settingsAction.path)}
              data-testid={`nav-${settingsAction.key}`}
              title="Settings"
              style={{ width: '80px', height: '90px' }}
            >
              <Settings className="text-gray-700" style={{ width: '48px', height: '48px' }} />
              <span className="text-xs text-gray-500 font-medium">Settings</span>
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-4 gap-3 mb-6">
            <Card className="py-2">
              <CardHeader className="pb-1 pt-2">
                <CardTitle className="text-xs font-medium text-slate-500">Customers</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-2">
                <p className="text-2xl font-bold" style={{ color: '#6B9F3E' }}>{stats.customers}</p>
              </CardContent>
            </Card>
            <Card className="py-2">
              <CardHeader className="pb-1 pt-2">
                <CardTitle className="text-xs font-medium text-slate-500">Locations</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-2">
                <p className="text-2xl font-bold" style={{ color: '#6B9F3E' }}>{stats.locations}</p>
              </CardContent>
            </Card>
            <Card className="py-2">
              <CardHeader className="pb-1 pt-2">
                <CardTitle className="text-xs font-medium text-slate-500">Vehicles</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-2">
                <p className="text-2xl font-bold" style={{ color: '#6B9F3E' }}>{stats.vehicles}</p>
              </CardContent>
            </Card>
            <Card className="py-2">
              <CardHeader className="pb-1 pt-2">
                <CardTitle className="text-xs font-medium text-slate-500">Active Apps</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-2">
                <p className="text-2xl font-bold" style={{ color: '#6B9F3E' }}>{stats.active_apps}/{stats.apps}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Content Area */}
        <Routes>
          <Route path="/" element={<AppLauncher />} />
          <Route path="/status" element={<SystemStatus />} />
          <Route path="/users" element={<Navigate to="/tfs-admin?tab=capabilities" replace />} />
          <Route path="/users/*" element={<Navigate to="/tfs-admin?tab=capabilities" replace />} />
          <Route path="/service-locations" element={<ServiceLocationManagement />} />
          <Route path="/followups" element={<FollowUpManagement />} />
          <Route path="/settings" element={<GlobalSettings />} />
          <Route path="/customers" element={<CustomerManagement />} />
          <Route path="/locations" element={<LocationManagement />} />
          <Route path="/vehicles" element={<VehicleManagement />} />
          <Route path="/apps" element={<AppManagement />} />
          <Route path="/submissions" element={<SubmissionRepository />} />
          <Route path="/work-orders" element={<AdminWorkOrderManagement />} />
          <Route path="/work-orders/new" element={<AdminWorkOrderForm />} />
          <Route path="/work-orders/edit" element={<AdminWorkOrderForm />} />
          <Route path="/work-orders/detail" element={<WorkOrderDetail />} />
          <Route path="/inspections" element={<VehicleInspectionManagement />} />
          <Route path="/feedback" element={<FeedbackRepository />} />
          <Route path="/evaluations" element={<PropertyEvaluationManagement />} />
          <Route path="/bank-pm" element={<BankPMManagement />} />
          <Route path="/incident-reports" element={<IncidentReportManagement />} />
          <Route path="/pmp-schools" element={<PMPSchoolsManagement />} />
          <Route path="/pmp-office" element={<PMPOfficeManagement />} />
          <Route path="/building-intelligence" element={<BuildingIntelligenceManagement />} />
          <Route path="/pre-applications" element={<PreApplicationManagement />} />
          <Route path="/cleaning-inspections" element={<InspectionManagement />} />
          <Route path="/inspections" element={<InspectionManagement />} />
          <Route path="/building-areas" element={<BuildingAreasManagement />} />
          <Route path="/event-service-surveys" element={<EventServiceSurveyManagement />} />
          <Route path="/client-communications" element={<ClientCommunicationManagement />} />
        </Routes>
      </div>
    </div>
  );
};

export default AdminDashboard;